import React from "react";
import { useTranslation } from "react-i18next";

import { PaiementPreviewData } from "./types";
import {
    PaymentPopupOverlay,
    PaymentPopupModal,
    PaymentPopupHeader,
    PaymentPopupTitle,
    PaymentAmountCard,
    PaymentAmountLabel,
    PaymentAmountValue,
    PaymentPopupSubtitle,
    PaymentMethodRow,
    PaymentMethodIcon,
    PaymentMethodInfo,
    PaymentMethodTitle,
    PaymentMethodDesc,
    PaymentRadio,
    PaymentOptionsSection,
    PaymentOptionsLabel,
    PaymentToggleRow,
    PaymentToggleInfo,
    PaymentToggleTitle,
    PaymentToggleDesc,
    PaymentToggleSwitch,
    PaymentPopupActionBtn,
    PaymentPopupBackBtn,
} from "./styled";

type PaymentPopupProps = {
    showPaymentPopup: boolean;
    paiementData?: PaiementPreviewData | null;
    primaryColor: string;
};

export function PaymentPopup({
    showPaymentPopup,
    paiementData,
    primaryColor,
}: PaymentPopupProps): JSX.Element | null {
    const { t } = useTranslation();

    // Payment popup screen state
    const [paymentPopupScreen, setPaymentPopupScreen] = React.useState<1 | 2>(
        1
    );

    // Reset popup screen when paiementData changes
    React.useEffect(() => {
        setPaymentPopupScreen(1);
    }, [paiementData?.splitPaymentEnabled]);

    if (
        !showPaymentPopup ||
        !paiementData ||
        paiementData.paymentEnabled === false
    ) {
        return null;
    }

    return (
        <PaymentPopupOverlay>
            <PaymentPopupModal>
                {paymentPopupScreen === 1 ? (
                    <>
                        <PaymentPopupHeader>
                            <PaymentPopupTitle>
                                {t("Choose your payment method")}
                            </PaymentPopupTitle>
                        </PaymentPopupHeader>
                        <PaymentAmountCard mainColor={primaryColor}>
                            <PaymentAmountLabel>
                                {t("Amount to pay")}
                            </PaymentAmountLabel>
                            <PaymentAmountValue
                                mainColor={primaryColor}
                            >
                                74,50 €
                            </PaymentAmountValue>
                        </PaymentAmountCard>
                        <PaymentPopupSubtitle>
                            {t("Select your payment method")}
                        </PaymentPopupSubtitle>
                        {paiementData.paymentMethods
                            .filter((m: any) => m.enabled)
                            .map((method: any, idx: number) => (
                                <PaymentMethodRow
                                    key={method.id}
                                    isSelected={idx === 0}
                                    mainColor={primaryColor}
                                >
                                    <PaymentMethodIcon
                                        mainColor={primaryColor}
                                    >
                                        <svg viewBox="0 0 24 24">
                                            <path d={method.icon} />
                                        </svg>
                                    </PaymentMethodIcon>
                                    <PaymentMethodInfo>
                                        <PaymentMethodTitle>
                                            {method.title}
                                        </PaymentMethodTitle>
                                        {method.descriptionVisible !==
                                            false && (
                                            <PaymentMethodDesc>
                                                {method.description}
                                            </PaymentMethodDesc>
                                        )}
                                    </PaymentMethodInfo>
                                    <PaymentRadio
                                        isSelected={idx === 0}
                                        mainColor={primaryColor}
                                    />
                                </PaymentMethodRow>
                            ))}
                        {paiementData.splitPaymentEnabled && (
                            <PaymentOptionsSection>
                                <PaymentOptionsLabel>
                                    OPTIONS
                                </PaymentOptionsLabel>
                                <PaymentToggleRow>
                                    <PaymentToggleInfo>
                                        <PaymentToggleTitle>
                                            {t("Split payment")}
                                        </PaymentToggleTitle>
                                        <PaymentToggleDesc>
                                            {t("Share the bill")}
                                        </PaymentToggleDesc>
                                    </PaymentToggleInfo>
                                    <PaymentToggleSwitch
                                        isOn={true}
                                        mainColor={primaryColor}
                                    />
                                </PaymentToggleRow>
                            </PaymentOptionsSection>
                        )}
                        <PaymentPopupActionBtn
                            mainColor={primaryColor}
                            onClick={() => {
                                if (
                                    paiementData.splitPaymentEnabled
                                ) {
                                    setPaymentPopupScreen(2);
                                }
                            }}
                        >
                            {paiementData.splitPaymentEnabled
                                ? t("Next") + " →"
                                : t("Validate")}
                        </PaymentPopupActionBtn>
                    </>
                ) : (
                    <>
                        <PaymentPopupHeader>
                            <PaymentPopupTitle>
                                {t("How to share the bill?")}
                            </PaymentPopupTitle>
                        </PaymentPopupHeader>
                        <PaymentAmountCard mainColor={primaryColor}>
                            <PaymentAmountLabel>
                                {t("Amount to pay")}
                            </PaymentAmountLabel>
                            <PaymentAmountValue
                                mainColor={primaryColor}
                            >
                                74,50 €
                            </PaymentAmountValue>
                        </PaymentAmountCard>
                        <PaymentPopupSubtitle>
                            {t("Choose a split option")}
                        </PaymentPopupSubtitle>
                        {paiementData.splitOptions
                            .filter((o: any) => o.enabled)
                            .map((option: any, idx: number) => (
                                <PaymentMethodRow
                                    key={option.id}
                                    isSelected={idx === 0}
                                    mainColor={primaryColor}
                                >
                                    <PaymentMethodIcon
                                        mainColor={primaryColor}
                                    >
                                        <svg viewBox="0 0 24 24">
                                            <path d={option.icon} />
                                        </svg>
                                    </PaymentMethodIcon>
                                    <PaymentMethodInfo>
                                        <PaymentMethodTitle>
                                            {option.title}
                                        </PaymentMethodTitle>
                                        {option.descriptionVisible !==
                                            false && (
                                            <PaymentMethodDesc>
                                                {option.description}
                                            </PaymentMethodDesc>
                                        )}
                                    </PaymentMethodInfo>
                                    <PaymentRadio
                                        isSelected={idx === 0}
                                        mainColor={primaryColor}
                                    />
                                </PaymentMethodRow>
                            ))}
                        <PaymentPopupActionBtn
                            mainColor={primaryColor}
                        >
                            {t("Validate")}
                        </PaymentPopupActionBtn>
                        <PaymentPopupBackBtn
                            onClick={() => setPaymentPopupScreen(1)}
                        >
                            <svg
                                width="10"
                                height="10"
                                viewBox="0 0 24 24"
                                fill="#666"
                            >
                                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                            </svg>
                            {t("Back")}
                        </PaymentPopupBackBtn>
                    </>
                )}
            </PaymentPopupModal>
        </PaymentPopupOverlay>
    );
}
